import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Grid3x3, List, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";

type CategoriesFilterProps = {
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
};

export function CategoriesFilter({
  viewMode,
  onViewModeChange,
}: CategoriesFilterProps) {
  const nav = useNavigate({ from: "/app/forms/categories" });
  const search = useSearch({ from: "/app/forms/categories" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort as
          | Array<{ field: string; dir: "asc" | "desc" }>
          | undefined,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const hasFilter = !!nameFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຫມວດຫມູ່ຕາມຊື່, ID ຫຼືແທັກ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">ມຸມມອງ:</span>
          <div className="flex gap-1 rounded-md border p-1">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onViewModeChange("grid")}
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onViewModeChange("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                nav({
                  search: {
                    offset: 0,
                    limit: search.limit ?? 20,
                    sort: search.sort as
                      | Array<{ field: string; dir: "asc" | "desc" }>
                      | undefined,
                    filters: undefined,
                  },
                })
              }
              className="max-sm:w-full"
            >
              <XIcon className="h-4 w-4" /> ລ້າງ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
